/**
 * Build a best-effort cloud provider console URL for a finding's resource.
 * Returns null when we don't have enough information to build a deep link.
 *
 * ``defaultRegion`` (optional): the region configured on the finding's parent
 * cloud account. Used as a fallback ONLY when the finding itself carries no
 * region info. Previously we hardcoded ``us-east-1`` which confused customers
 * who had (say) ``us-east-2`` configured on their account — clicking Console
 * on a global-service finding (IAM) landed them in ``us-east-1.console.…``
 * and they read the URL as "this finding is from us-east-1".
 *
 * For truly global AWS services (IAM), the console URL now omits the region
 * subdomain entirely — AWS resolves ``console.aws.amazon.com/iam/…`` to the
 * signed-in user's default region without lying about which region "owns"
 * the resource.
 */

// AWS resource type → console path.
// Signature: (id, region) → url. ``region`` may be ``null`` for global services.
const AWS_CONSOLE_PATHS = {
  's3': (id) => `https://s3.console.aws.amazon.com/s3/buckets/${encodeURIComponent(id)}`,
  's3-bucket': (id) => `https://s3.console.aws.amazon.com/s3/buckets/${encodeURIComponent(id)}`,
  'ec2': (id, region) => `https://${region || 'console'}.${region ? 'console.aws.amazon.com' : 'aws.amazon.com'}/ec2/home${region ? `?region=${region}` : ''}#Instances:search=${encodeURIComponent(id)}`,
  'ec2-instance': (id, region) => `https://${region || 'console'}.${region ? 'console.aws.amazon.com' : 'aws.amazon.com'}/ec2/home${region ? `?region=${region}` : ''}#Instances:search=${encodeURIComponent(id)}`,
  'security-group': (id, region) => `https://${region || 'console'}.${region ? 'console.aws.amazon.com' : 'aws.amazon.com'}/ec2/home${region ? `?region=${region}` : ''}#SecurityGroups:search=${encodeURIComponent(id)}`,
  // IAM is a global service — never scope the URL to a specific region.
  'iam': (id) => `https://console.aws.amazon.com/iam/home#/users/${encodeURIComponent(id)}`,
  'iam-user': (id) => `https://console.aws.amazon.com/iam/home#/users/${encodeURIComponent(id)}`,
  'iam-role': (id) => `https://console.aws.amazon.com/iam/home#/roles/${encodeURIComponent(id)}`,
  'user': (id) => `https://console.aws.amazon.com/iam/home#/users/${encodeURIComponent(id)}`,
  'role': (id) => `https://console.aws.amazon.com/iam/home#/roles/${encodeURIComponent(id)}`,
  'account-password-policy': () => `https://console.aws.amazon.com/iam/home#/account_settings`,
  'rds': (id, region) => `https://${region || 'console'}.${region ? 'console.aws.amazon.com' : 'aws.amazon.com'}/rds/home${region ? `?region=${region}` : ''}#database:id=${encodeURIComponent(id)}`,
  'rds-instance': (id, region) => `https://${region || 'console'}.${region ? 'console.aws.amazon.com' : 'aws.amazon.com'}/rds/home${region ? `?region=${region}` : ''}#database:id=${encodeURIComponent(id)}`,
  'db': (id, region) => `https://${region || 'console'}.${region ? 'console.aws.amazon.com' : 'aws.amazon.com'}/rds/home${region ? `?region=${region}` : ''}#database:id=${encodeURIComponent(id)}`,
  'cloudtrail': (id, region) => `https://${region || 'console'}.${region ? 'console.aws.amazon.com' : 'aws.amazon.com'}/cloudtrail/home${region ? `?region=${region}` : ''}#/trails`,
  'trail': (id, region) => `https://${region || 'console'}.${region ? 'console.aws.amazon.com' : 'aws.amazon.com'}/cloudtrail/home${region ? `?region=${region}` : ''}#/trails`,
  'kms': (id, region) => `https://${region || 'console'}.${region ? 'console.aws.amazon.com' : 'aws.amazon.com'}/kms/home${region ? `?region=${region}` : ''}#/kms/keys/${encodeURIComponent(id)}`,
  'key': (id, region) => `https://${region || 'console'}.${region ? 'console.aws.amazon.com' : 'aws.amazon.com'}/kms/home${region ? `?region=${region}` : ''}#/kms/keys/${encodeURIComponent(id)}`,
  'lambda': (id, region) => `https://${region || 'console'}.${region ? 'console.aws.amazon.com' : 'aws.amazon.com'}/lambda/home${region ? `?region=${region}` : ''}#/functions/${encodeURIComponent(id)}`,
  'vpc': (id, region) => `https://${region || 'console'}.${region ? 'console.aws.amazon.com' : 'aws.amazon.com'}/vpc/home${region ? `?region=${region}` : ''}#vpcs:search=${encodeURIComponent(id)}`,
  'volume': (id, region) => `https://${region || 'console'}.${region ? 'console.aws.amazon.com' : 'aws.amazon.com'}/ec2/home${region ? `?region=${region}` : ''}#Volumes:search=${encodeURIComponent(id)}`,
};

// Global AWS services whose URLs must not include a region subdomain, even if
// the caller passes a region. Prevents lies like "IAM finding from us-east-1"
// when the account is actually configured to us-east-2.
const AWS_GLOBAL_SERVICES = new Set(['iam', 's3']);

function awsConsoleFromArn(arn, region) {
  // arn:aws:<service>:<region>:<account>:<resource-type>/<resource-id> OR :<resource-id>
  const m = arn.match(/^arn:aws:([^:]+):([^:]*):([^:]*):(.+)$/);
  if (!m) return null;
  const [, service, arnRegion, , resourcePart] = m;
  // Prefer the ARN's embedded region, then the caller-supplied region.
  // Do NOT fall back to a hardcoded 'us-east-1' — that misled users who
  // configured a different region into thinking findings came from us-east-1.
  const svcLower = service.toLowerCase();
  const reg = AWS_GLOBAL_SERVICES.has(svcLower) ? null : (arnRegion || region || null);
  // Detect resource type subsegment (e.g., "security-group/sg-...", "trail/main", "user/alice")
  let resourceType = '';
  let id = resourcePart;
  if (resourcePart.includes('/')) {
    const parts = resourcePart.split('/');
    resourceType = parts[0];
    id = parts.slice(1).join('/');
  } else if (resourcePart.includes(':')) {
    const parts = resourcePart.split(':');
    resourceType = parts[0];
    id = parts.slice(1).join(':');
  } else {
    // Some AWS resource types have no id separator (e.g. "account-password-policy").
    // Try the resourcePart itself as a handler key.
    resourceType = resourcePart;
  }
  // Prefer resource-type-specific handler over service-level
  const lookupKey = (resourceType || service).toLowerCase();
  const handler = AWS_CONSOLE_PATHS[lookupKey] || AWS_CONSOLE_PATHS[svcLower];
  if (handler) return handler(id, reg);
  // Fallback to resource groups view — no region subdomain when unknown.
  return reg
    ? `https://${reg}.console.aws.amazon.com/resource-groups/resources/?region=${reg}`
    : `https://console.aws.amazon.com/resource-groups/resources/`;
}

function azureConsoleFromResourceId(resourceId) {
  // Azure ResourceID looks like /subscriptions/.../resourceGroups/.../providers/.../<name>
  if (!resourceId) return null;
  return `https://portal.azure.com/#resource${resourceId.startsWith('/') ? '' : '/'}${resourceId}`;
}

function gcpConsoleFromResource(resourceId) {
  // GCP resource names often look like //compute.googleapis.com/projects/<project>/zones/<zone>/instances/<name>
  if (!resourceId) return null;
  const cleaned = resourceId.startsWith('//') ? resourceId.slice(2) : resourceId;
  const projectMatch = cleaned.match(/projects\/([^/]+)/);
  if (projectMatch) {
    return `https://console.cloud.google.com/home/dashboard?project=${projectMatch[1]}`;
  }
  return 'https://console.cloud.google.com/';
}

/**
 * @param {object} finding - normalized finding doc
 * @param {object} [options]
 * @param {string} [options.defaultRegion] - account's configured region. Used ONLY
 *   when the finding itself carries no region info. Truly global services (IAM)
 *   ignore this — their URLs never carry a region.
 * @returns {string|null} - deep link to the cloud console for this resource
 */
export function buildConsoleUrl(finding, options = {}) {
  if (!finding) return null;
  const res = finding.resource || {};
  const provider = (finding.provider || res.provider || '').toLowerCase();
  const resourceArn = res.resource_arn;
  const resourceId = res.resource_id || finding.resource_id;
  // Do not hardcode us-east-1: prefer finding's own region → account's
  // configured region → null (URL will omit the region subdomain).
  const findingRegion = res.region || finding.region;
  const normalisedRegion = (findingRegion && findingRegion !== 'global') ? findingRegion : null;
  const region = normalisedRegion || options.defaultRegion || null;

  if (provider === 'aws') {
    if (resourceArn?.startsWith('arn:aws:')) return awsConsoleFromArn(resourceArn, region);
    // Try resource type mapping by service field
    const svc = (finding.service || res.resource_type || '').toLowerCase();
    const handler = AWS_CONSOLE_PATHS[svc];
    if (handler && resourceId) {
      const isGlobal = AWS_GLOBAL_SERVICES.has(svc) || svc.startsWith('iam');
      return handler(resourceId, isGlobal ? null : region);
    }
    return `https://console.aws.amazon.com/`;
  }
  if (provider === 'azure') {
    return azureConsoleFromResourceId(resourceArn || resourceId);
  }
  if (provider === 'gcp') {
    return gcpConsoleFromResource(resourceArn || resourceId);
  }
  return null;
}
